import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Database, Download, Upload, Trash2, RefreshCw, Clock, Edit2, Check, X } from 'lucide-react';

const DatabaseBackup = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackupId, setSelectedBackupId] = useState(null);
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const [dbInfo, setDbInfo] = useState({ db_name: '', environment: '', is_production: true });
  const [restoreOptions, setRestoreOptions] = useState({
    preserve_users: true,
    preserve_investments: true,
    preserve_backups: true
  });

  useEffect(() => {
    fetchBackups();
    fetchDatabaseInfo();
  }, []);

  const fetchDatabaseInfo = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/database-info`);
      setDbInfo(response.data);
    } catch (error) {
      console.error('Error loading database info:', error);
    }
  };


  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/backups`);
      setBackups(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Backups');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    if (!window.confirm('Möchten Sie jetzt ein Backup erstellen?')) {
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/superadmin/backup`, {
        includes: ['database'],
        description: 'Manuelles Backup',
        upload_to_cloud: false
      });
      toast.success(response.data.message || 'Backup erfolgreich erstellt');
      fetchBackups();
    } catch (error) {
      // Handle validation errors (array of error objects)
      if (error.response?.data && Array.isArray(error.response.data)) {
        const errorMsg = error.response.data.map(e => e.msg).join(', ');
        toast.error(`Validierungsfehler: ${errorMsg}`);
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
      }
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      const response = await axios.get(`${API}/superadmin/backup/${backupId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${backupId}.tar.gz`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup wird heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Backups');
    }
  };

  const openRestoreDialog = (backupId) => {
    setSelectedBackupId(backupId);
    setRestoreDialogOpen(true);
  };

  const restoreBackup = async () => {
    if (!selectedBackupId) return;

    try {
      const response = await axios.post(`${API}/superadmin/restore/${selectedBackupId}`, restoreOptions);
      toast.success(response.data.message || 'Datenbank erfolgreich wiederhergestellt');
      setRestoreDialogOpen(false);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Restore error:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Wiederherstellen der Datenbank';
      toast.error(errorMessage);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm(`Backup "${backupId}" wirklich löschen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/superadmin/backups/${backupId}`);
      toast.success('Backup gelöscht');
      fetchBackups();
    } catch (error) {
      console.error('Delete backup error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Backups');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file extension
    if (!file.name.endsWith('.tar.gz')) {
      toast.error('Nur .tar.gz Dateien sind erlaubt');
      event.target.value = '';
      return;
    }

    // Check file size (limit to 500MB for safety)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      toast.error('Datei ist zu groß (max. 500MB)');
      event.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/superadmin/backup/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      toast.success(response.data.message || 'Backup erfolgreich hochgeladen');
      fetchBackups();
      event.target.value = ''; // Reset file input
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen des Backups');
      event.target.value = '';
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const startEditDescription = (backupId, currentDescription) => {
    setEditingDescriptionId(backupId);
    setEditDescriptionValue(currentDescription || '');
  };

  const cancelEditDescription = () => {
    setEditingDescriptionId(null);
    setEditDescriptionValue('');
  };

  const saveDescription = async (backupId) => {
    try {
      await axios.put(`${API}/superadmin/backup/${backupId}/description`, null, {
        params: { description: editDescriptionValue }
      });
      toast.success('Beschreibung aktualisiert');
      setEditingDescriptionId(null);
      fetchBackups();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Beschreibung');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate('/bess/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2" size={18} />
          Zurück zum Dashboard
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Database size={32} className="text-blue-600" />
              Datenbank-Sicherung
            </h1>
            <p className="text-slate-600 mt-2">Verwalten Sie Datenbank-Backups und Wiederherstellungen</p>
            {dbInfo.db_name && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">
                  Datenbank: <span className="font-bold text-slate-900">{dbInfo.db_name}</span>
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  dbInfo.is_production 
                    ? 'bg-red-100 text-red-800 border border-red-300' 
                    : 'bg-green-100 text-green-800 border border-green-300'
                }`}>
                  {dbInfo.is_production ? '🔴 PRODUKTIV' : '🟢 TEST'}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchBackups} variant="outline" disabled={loading}>
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Aktualisieren
            </Button>
            <label htmlFor="backup-upload">
              <Button 
                onClick={() => document.getElementById('backup-upload').click()} 
                variant="outline"
                disabled={uploading}
                className="cursor-pointer"
              >
                <Upload className="mr-2" size={18} />
                {uploading ? `Hochladen... ${uploadProgress}%` : 'Backup hochladen'}
              </Button>
            </label>
            <input
              id="backup-upload"
              type="file"
              accept=".tar.gz"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button onClick={createBackup} disabled={creating} className="bg-blue-600 hover:bg-blue-700">
              <Database className="mr-2" size={18} />
              {creating ? 'Erstelle Backup...' : 'Neues Backup'}
            </Button>
          </div>
        </div>

        {/* Info Box */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="bg-blue-500 text-white rounded-full p-2">
                <Database size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Über Datenbank-Backups</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Backups enthalten die komplette MongoDB-Datenbank</li>
                  <li>• Automatische Komprimierung als .tar.gz Archiv</li>
                  <li>• Backups können erstellt, hochgeladen oder in Azure Cloud gespeichert werden</li>
                  <li>• 💾 Hochgeladene Backups können nach einem Fork wiederverwendet werden</li>
                  <li>• ⚠️ Wiederherstellung überschreibt ALLE aktuellen Daten</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card>
          <CardHeader>
            <CardTitle>Verfügbare Backups ({backups.length})</CardTitle>
            <CardDescription>Klicken Sie auf die Aktionen, um Backups zu verwalten</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto mb-4" size={40} />
                <p className="text-slate-600">Lade Backups...</p>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-12">
                <Database size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600 mb-4">Keine Backups vorhanden</p>
                <Button onClick={createBackup}>
                  <Database className="mr-2" size={18} />
                  Erstes Backup erstellen
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.id}
                    className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Database className="text-blue-600" size={24} />
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{backup.name || backup.id}</h3>
                            
                            {/* Editable Description */}
                            {editingDescriptionId === backup.id ? (
                              <div className="flex items-center gap-2 mt-1">
                                <input
                                  type="text"
                                  value={editDescriptionValue}
                                  onChange={(e) => setEditDescriptionValue(e.target.value)}
                                  className="text-sm border rounded px-2 py-1 flex-1"
                                  placeholder="Beschreibung eingeben..."
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveDescription(backup.id);
                                    if (e.key === 'Escape') cancelEditDescription();
                                  }}
                                />
                                <button
                                  onClick={() => saveDescription(backup.id)}
                                  className="text-green-600 hover:text-green-700 p-1"
                                  title="Speichern"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  onClick={cancelEditDescription}
                                  className="text-red-600 hover:text-red-700 p-1"
                                  title="Abbrechen"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <div className="mt-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-slate-600 italic">
                                    {backup.description || 'Keine Beschreibung'}
                                  </p>
                                  <button
                                    onClick={() => startEditDescription(backup.id, backup.description)}
                                    className="text-slate-400 hover:text-blue-600 transition-colors"
                                    title="Beschreibung bearbeiten"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">ID: {backup.id}</p>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {formatDate(backup.created_at)}
                              </span>
                              <span className="font-medium text-blue-600">
                                {formatBytes(backup.size_bytes)}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                backup.type === 'manual' ? 'bg-blue-100 text-blue-800' :
                                backup.type === 'uploaded' ? 'bg-orange-100 text-orange-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {backup.type === 'manual' ? 'Manuell' : 
                                 backup.type === 'uploaded' ? 'Hochgeladen' : 
                                 'Automatisch'}
                              </span>
                              {backup.cloud_uploaded && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  Cloud
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadBackup(backup.id)}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Backup herunterladen"
                        >
                          <Download size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRestoreDialog(backup.id)}
                          className="text-green-600 hover:bg-green-50"
                          title="Datenbank wiederherstellen"
                        >
                          <Upload size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteBackup(backup.id)}
                          className="text-red-600 hover:bg-red-50"
                          title="Backup löschen"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Restore Dialog */}
      {restoreDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-red-600">⚠️ Backup Wiederherstellen</h3>
            
            <div className="mb-4">
              <p className="text-sm text-slate-600 mb-4">
                Sie sind dabei, das Backup <strong>{selectedBackupId}</strong> wiederherzustellen.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800 font-semibold">
                  ⚠️ ACHTUNG: Dies überschreibt die aktuellen Daten in der Datenbank!
                </p>
              </div>

              <div className="space-y-3 mb-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={restoreOptions.preserve_users}
                    onChange={(e) => setRestoreOptions({...restoreOptions, preserve_users: e.target.checked})}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-semibold text-sm">👥 Benutzer beibehalten</div>
                    <div className="text-xs text-slate-500">
                      Aktuelle Benutzer bleiben erhalten und werden nicht aus dem Backup wiederhergestellt
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={restoreOptions.preserve_investments}
                    onChange={(e) => setRestoreOptions({...restoreOptions, preserve_investments: e.target.checked})}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-semibold text-sm">💰 Investitionen beibehalten</div>
                    <div className="text-xs text-slate-500">
                      Aktuelle Investitionen bleiben erhalten und werden nicht aus dem Backup wiederhergestellt
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={restoreOptions.preserve_backups}
                    onChange={(e) => setRestoreOptions({...restoreOptions, preserve_backups: e.target.checked})}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-semibold text-sm">💾 Backup-Historie beibehalten</div>
                    <div className="text-xs text-slate-500">
                      Aktuelle Backup-Liste bleibt sichtbar
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setRestoreDialogOpen(false);
                  setSelectedBackupId(null);
                }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={restoreBackup}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Jetzt Wiederherstellen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseBackup;
